import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import validator from "validator";
import { useNavigate, Link } from "react-router-dom";
import { mailVerification, registerCustomer } from "../../../lib/webService/webService";
import { toast } from "../../../utilities/toast";
import { Eye, EyeSlash, CheckCircleFill } from "react-bootstrap-icons";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Constants ────────────────────────────────────────────────────────────────

const PASSWORD_RULES = {
  MIN_LENGTH: 6,
  MAX_LENGTH: 16,
  SPECIAL_CHARS: /[!@#$%^&*(),.?":{}|<>]/,
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  NUMBER: /\d/,
};

const FIREBASE_ERROR_MESSAGES = {
  "auth/email-already-in-use":    "An account already exists with this email",
  "auth/invalid-email":           "Invalid email address format",
  "auth/operation-not-allowed":   "Email/password accounts are not enabled",
  "auth/weak-password":           "Password is too weak",
  "auth/network-request-failed":  "Network error. Check your connection",
  "auth/too-many-requests":       "Too many attempts. Try again later",
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const pageWrap = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
};

const card = {
  backgroundColor: "#fff",
  borderRadius: 18,
  border: "1px solid rgba(15,23,42,0.07)",
  padding: "28px 24px 24px",
  boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
};

const labelStyle = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "rgba(60,60,67,0.55)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "block",
  marginBottom: 6,
};

const inputStyle = {
  backgroundColor: "rgba(118,118,128,0.1)",
  border: "1px solid transparent",
  borderRadius: 10,
  padding: "11px 14px",
  fontSize: "0.9375rem",
  color: "#1c1c1e",
  width: "100%",
};

const primaryBtn = {
  width: "100%",
  borderRadius: 12,
  padding: "13px",
  fontSize: "0.9375rem",
  fontWeight: 600,
  border: "none",
};

// ─── Password rules grid ──────────────────────────────────────────────────────

const RULE_DEFS = [
  { key: "minLength",      label: `${PASSWORD_RULES.MIN_LENGTH}+ characters` },
  { key: "maxLength",      label: `Max ${PASSWORD_RULES.MAX_LENGTH} chars`   },
  { key: "hasNumber",      label: "Number"                                   },
  { key: "hasSpecialChar", label: "Special character"                        },
  { key: "hasUpperCase",   label: "Uppercase letter"                         },
  { key: "hasLowerCase",   label: "Lowercase letter"                         },
  { key: "passwordsMatch", label: "Passwords match"                          },
];

const PasswordRulesList = ({ passwordRules }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px 10px", marginBottom: 20 }}>
    {RULE_DEFS.map(({ key, label }) => {
      const met = passwordRules[key];
      return (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{
            width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
            backgroundColor: met ? "rgba(52,199,89,0.14)" : "rgba(60,60,67,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {met
              ? <CheckCircleFill size={10} color="#34c759" />
              : <div style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "rgba(60,60,67,0.3)" }} />
            }
          </div>
          <span style={{ fontSize: "0.78rem", color: met ? "#34c759" : "rgba(60,60,67,0.5)", fontWeight: met ? 500 : 400 }}>
            {label}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── Register ─────────────────────────────────────────────────────────────────

const Register = () => {
  const [formData, setFormData] = useState({ email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState({ isErr: false, message: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordRules, setPasswordRules] = useState({
    minLength: false, maxLength: false, hasNumber: false,
    hasSpecialChar: false, hasUpperCase: false, hasLowerCase: false, passwordsMatch: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const { password, confirmPassword } = formData;
    setPasswordRules({
      minLength:      password.length >= PASSWORD_RULES.MIN_LENGTH,
      maxLength:      password.length <= PASSWORD_RULES.MAX_LENGTH && password.length > 0,
      hasNumber:      PASSWORD_RULES.NUMBER.test(password),
      hasSpecialChar: PASSWORD_RULES.SPECIAL_CHARS.test(password),
      hasUpperCase:   PASSWORD_RULES.UPPERCASE.test(password),
      hasLowerCase:   PASSWORD_RULES.LOWERCASE.test(password),
      passwordsMatch: password === confirmPassword && password !== "",
    });
  }, [formData.password, formData.confirmPassword]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const showError = (message) => {
    toast.error(message);
    setError({ isErr: true, message });
  };

  const validateForm = () => {
    const { email, password, confirmPassword } = formData;
    if (!email || !password || !confirmPassword) { showError("All fields are required"); return false; }
    if (!validator.isEmail(email))               { showError("Invalid email format"); return false; }
    if (password !== confirmPassword)            { showError("Passwords do not match"); return false; }
    if (password.length < PASSWORD_RULES.MIN_LENGTH) { showError(`Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters`); return false; }
    if (password.length > PASSWORD_RULES.MAX_LENGTH) { showError(`Password must not exceed ${PASSWORD_RULES.MAX_LENGTH} characters`); return false; }
    return true;
  };

  const registerUser = async () => {
    try {
      const { email, password } = formData;
      const data = await registerCustomer(email, password);
      await mailVerification(email, data.actionLink);
      toast.success("Account created! Verification link sent to your email.");
      navigate("/login");
    } catch (error) {
      console.error("Registration error:", error);
      const msg = FIREBASE_ERROR_MESSAGES[error.code] ?? "Failed to create account. Please try again";
      toast.error(msg);
      setError({ isErr: true, message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError({ isErr: false, message: "" });
    if (!validateForm()) return;
    setLoading(true);
    await registerUser();
  };

  const allRulesMet = Object.values(passwordRules).every(Boolean);

  return (
    <div style={pageWrap}>
      <Seo {...generateNoIndexSEO("Sign Up")} />
      <div style={{ width: "100%", maxWidth: 380 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src="/imgs/logo/complete-250.png" alt="FineEats" style={{ height: "clamp(88px, 24vw, 120px)", width: "auto", maxWidth: "100%" }} />
        </div>

        {/* Card */}
        <div style={card}>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em", textAlign: "center", marginBottom: 4 }}>
            Create Account
          </h1>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", textAlign: "center", marginBottom: 22 }}>
            Sign up to get started
          </p>

          {error.isErr && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setError({ isErr: false, message: "" })}
              style={{ borderRadius: 10, fontSize: "0.875rem", marginBottom: 18, padding: "10px 14px" }}
            >
              {error.message}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email</label>
              <Form.Control
                type="email"
                name="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create a password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  style={{ ...inputStyle, paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "rgba(60,60,67,0.4)", lineHeight: 1 }}
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Confirm Password</label>
              <Form.Control
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Repeat your password"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
            </div>

            {/* Password rules */}
            <PasswordRulesList passwordRules={passwordRules} />

            {/* Submit */}
            <Button type="submit" variant="primary" disabled={!allRulesMet || loading} style={primaryBtn}>
              {loading
                ? <><Spinner size="sm" animation="border" className="me-2" />Creating account…</>
                : "Create Account"
              }
            </Button>
          </Form>
        </div>

        {/* Footer */}
        <p style={{ textAlign: "center", marginTop: 18, fontSize: "0.875rem", color: "rgba(60,60,67,0.6)" }}>
          Already have an account?{" "}
          <Link to="/login" style={{ color: "#007aff", textDecoration: "none", fontWeight: 500 }}>
            Sign in
          </Link>
        </p>

      </div>
    </div>
  );
};

export default Register;
